import { PlusOutlined } from "@ant-design/icons";
import { useGetPermissionGroups } from "@entities/permissionGroups/lib/hooks";
import { useAuth } from "@entities/auth";
import { PermissionGroups } from "@pages/Admin/Permission";
import { countryCodeOptions } from "@shared/lib/constants";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { CreateAdmin } from "@shared/lib/types";
import {
  Button,
  Divider,
  Drawer,
  Flex,
  Form,
  Image,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Upload
} from "antd";
import { useState } from "react";
import styles from "./styles.module.scss";

const { Option } = Select;

interface AdminFormProps {
  onSubmit: (values: CreateAdmin) => void;
  isSubmitting: boolean;
  roles: string[];
}

export function AdminForm({
  onSubmit,
  isSubmitting,
  roles: initialRoles,
}: AdminFormProps) {
  const [form] = Form.useForm();
  const { beUser } = useAuth();
  const isSuperAdmin = beUser?.permissions?.includes("super");
  const isPermissionGroupsEnabled = beUser?.permissions?.includes("view-permission-module");

  const [profilePicUrl, setProfilePicUrl] = useState("");
  const [newRole, setNewRole] = useState("");
  const [availableRoles, setAvailableRoles] = useState(initialRoles);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openPermissionsDrawer, setOpenPermissionsDrawer] = useState<boolean>(false);

  const { permissionGroups, isFetching: isPermissionGroupsFetching } =
    useGetPermissionGroups();

  const handleImageUpload = async (file: File) => {
    try {
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "profile-pictures",
        file
      );
      setProfilePicUrl(uploadedUrl);
    } catch (error) {
      ToastService.error("Image upload failed. Please try again.");
    }
  };

  const handleDeleteImage = () => {
    setProfilePicUrl("");
    ToastService.success("Profile picture removed successfully.");
  };
  const addNewRole = (roleName: string) => {
    if (!roleName.trim() || availableRoles.includes(roleName)) {
      return;
    }

    const updatedRoles = [...availableRoles, roleName];
    setAvailableRoles(updatedRoles);
    setNewRole("");
    form.setFieldsValue({ role: roleName });
    setDropdownOpen(false);
  };

  const handleSubmit = (values: any) => {
    onSubmit({ ...values, profilePicUrl });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
      // className={styles.form}
      initialValues={{ countryCode: "+91", emailNotificationActive: true }}
    >
      {/* First Name */}
      <Form.Item label="Profile Picture">
        <Upload
          accept="image/jpeg,image/jpg,image/png"
          onChange={(info) =>
            info.file && handleImageUpload(info.file.originFileObj!)
          }
          beforeUpload={(file) => {
            const isImage = ["image/jpeg", "image/jpg", "image/png"].includes(
              file.type
            );
            if (!isImage) {
              ToastService.error(
                "You can only upload JPG, JPEG, or PNG files!"
              );
            }
            return isImage; // Prevents upload if not a valid image
          }}
          showUploadList={false}
        >
          <Button> Upload</Button>
        </Upload>
        {profilePicUrl && (
          <div style={{ marginTop: "16px" }}>
            <Image src={profilePicUrl} alt="Profile" width={100} />
            <Popconfirm
              title="Are you sure to delete this image?"
              onConfirm={handleDeleteImage}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger>
                Delete Image
              </Button>
            </Popconfirm>
          </div>
        )}
      </Form.Item>
      <Form.Item
        name="firstName"
        label="First Name"
        rules={[{ required: true, message: "First name is required" }]}
      >
        <Input placeholder="First Name" />
      </Form.Item>

      {/* Last Name */}
      <Form.Item
        name="lastName"
        label="Last Name"
        rules={[{ required: true, message: "Last name is required" }]}
      >
        <Input placeholder="Last Name" />
      </Form.Item>

      {/* Email */}
      <Form.Item
        name="emailId"
        label="Email"
        rules={[
          { required: true, message: "Email is required" },
          { type: "email", message: "Invalid email address" },
          {
            pattern: /@(cloutflow\.com|thestage\.club)$/,
            message: "Email must end with @cloutflow.com or @thestage.club",
          },
        ]}
      >
        <Input type="email" placeholder="Email" />
      </Form.Item>

      {/* Password */}

      {/* Phone Number with Country Code */}
      <Form.Item label="Phone Number">
        <Input.Group compact>
          <Form.Item
            name="countryCode"
            noStyle
            rules={[{ required: true, message: "Country code is required" }]}
          >
            <Select style={{ width: "30%" }}>
              {countryCodeOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            noStyle
            rules={[
              { required: true, message: "Phone number is required" },
              { pattern: /^\d+$/, message: "Phone number must be numeric" },
            ]}
          >
            <Input style={{ width: "70%" }} placeholder="Phone Number" />
          </Form.Item>
        </Input.Group>
      </Form.Item>

      <Form.Item
        name="role"
        label="Role"
        rules={[{ required: true, message: "Role is required" }]}
      >
        <Select
          open={dropdownOpen}
          onDropdownVisibleChange={setDropdownOpen}
          showSearch
          placeholder="Select or create a role"
          optionFilterProp="children"
          allowClear
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <Space
                style={{
                  padding: "0 8px 4px",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Input
                  placeholder="Enter new role"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newRole) {
                      e.stopPropagation();
                      e.preventDefault();
                      addNewRole(newRole);
                    }
                  }}
                />
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  disabled={!newRole}
                  onClick={() => addNewRole(newRole)}
                >
                  Add Role
                </Button>
              </Space>
            </>
          )}
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {availableRoles.map((role) => (
            <Option key={role} value={role}>
              {role}
            </Option>
          ))}
        </Select>
      </Form.Item>
      {/* Profile Picture Upload */}

      <Form.Item
        label={<Flex align="center">
          <span>Permission Groups</span>
          {(isSuperAdmin || isPermissionGroupsEnabled) && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setOpenPermissionsDrawer(true);
              }}
              style={{ fontSize: 11 }}
            >
              Manage Permission
            </Button>
          )}
          </Flex>}
        name="permissionGroupIds"
        rules={[
          {
            required: true,
            message: "Permission groups are required",
          },
        ]}
      >
        <Select
          loading={isPermissionGroupsFetching}
          mode="multiple"
          maxCount={1}
          placeholder="Select permission groups"
          filterOption={(input, option) =>
            (option?.label as unknown as string)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          options={
            permissionGroups?.map((group) => ({
              label: group.name,
              value: group._id,
            })) || []
          }
        />
      </Form.Item>

      <Form.Item label="E-mail Notifications">
        <Row justify="space-between">
          <div className={styles.diasbledText}>
            Send user email Notifications
          </div>
          <Form.Item name="emailNotificationActive">
            <Switch />
          </Form.Item>
        </Row>
      </Form.Item>

      {/* Submit Button */}
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </Form.Item>
      <Drawer 
      open={openPermissionsDrawer}
      onClose={() => setOpenPermissionsDrawer(false)}
      title="Manage Permission Groups"
      destroyOnClose
      width={600}
      
      >
        <PermissionGroups />
      </Drawer>
    </Form>
  );
}
